import React from 'react';
import { Link } from 'react-router-dom';
import TestimonialSection from '../components/TestimonialSection';
import visionImage from '../images/NGO-First-Work.optimised.jpg';

const focusAreas = [
  {
    icon: 'fas fa-book-open',
    title: 'Free Education',
    text: 'Supporting access to free education and learning opportunities for children studying up to Class 8.',
    path: '/education/',
  },
  {
    icon: 'fas fa-book-reader',
    title: 'Learning Support',
    text: 'Helping schoolchildren build confidence, develop their potential and move towards a brighter future.',
    path: '/education/',
  },
  {
    icon: 'fas fa-star',
    title: 'Community Vision',
    text: 'Beginning with education while aspiring to contribute to wider community development over time.',
    path: '/about/',
  },
];

const programmes = [
  { icon: 'fas fa-graduation-cap', title: 'Education', status: 'Current focus', text: 'Supporting access to free education and learning opportunities for children studying up to Class 8.', path: '/education/' },
  { icon: 'fas fa-toolbox', title: 'Skill Development', status: 'Future vision', text: 'We hope to explore practical skills and vocational learning opportunities in the future.', path: null },
  { icon: 'fas fa-heart', title: 'Health', status: 'Future vision', text: 'We aspire to support health awareness and community wellbeing initiatives as our work expands.', path: '/health/' },
  { icon: 'fas fa-person-dress', title: 'Women Empowerment', status: 'Future vision', text: 'We hope to explore learning and empowerment opportunities for women in the future.', path: '/women-empowerment/' },
  { icon: 'fas fa-seedling', title: 'Environment', status: 'Future vision', text: 'Environmental awareness and sustainable community practices are part of our long-term vision.', path: '/environment/' },
  { icon: 'fas fa-hands-helping', title: 'Livelihood', status: 'Future vision', text: 'We aim to explore pathways to livelihood skills and economic opportunity in the future.', path: null },
];

const HomePage: React.FC = () => (
  <main className="sf-home">
    <section className="sf-home-hero tw-grid tw-grid-cols-1 tw-items-center tw-gap-8 tw-px-[5%] tw-pt-[2.7rem] tw-pb-[clamp(3.5rem,6vw,5.5rem)] xs:tw-gap-[clamp(2rem,4vw,4.5rem)] xs:tw-py-[clamp(3.5rem,6vw,5.5rem)] nav:tw-grid-cols-[1.1fr_0.9fr]">
      <div className="sf-home-hero-copy tw-max-w-[46rem]">
        <p className="sf-eyebrow">Education · Opportunity · Community</p>
        <h1 className="tw-mb-[1.1rem] tw-font-display tw-text-[clamp(2.15rem,10vw,2.5rem)] tw-font-bold tw-leading-[1.08] tw-tracking-[-0.025em] xs:tw-text-[clamp(2.45rem,5vw,4rem)] xs:tw-leading-[1.03] xs:tw-tracking-[-0.035em]">Free Education for Children.<br />A Brighter Future Through Learning.</h1>
        <p className="sf-home-hero-text tw-mb-6 tw-max-w-[36rem] tw-text-[.96rem] tw-leading-[1.65] xs:tw-text-base xs:tw-leading-[1.7]">
          Srisailam Foundation supports children studying up to Class 8 through free education and learning opportunities, helping them build a stronger foundation for their future.
        </p>
        <div className="sf-home-actions tw-flex tw-flex-wrap tw-gap-[0.7rem]">
          <Link className="sf-button sf-button-primary" to="/education/">Explore Education</Link>
          <Link className="sf-button sf-button-outline" to="/about/">Learn About Us</Link>
        </div>
        <div className="sf-home-tags tw-mt-[1.3rem] tw-flex tw-flex-wrap tw-gap-[0.4rem] xs:tw-gap-[0.5rem]" aria-label="Areas of focus">
          <span className="tw-px-[.58rem] tw-py-[.3rem] tw-text-[.69rem] xs:tw-px-[.66rem] xs:tw-py-[.32rem] xs:tw-text-[.72rem]">Free Education</span><span className="tw-px-[.58rem] tw-py-[.3rem] tw-text-[.69rem] xs:tw-px-[.66rem] xs:tw-py-[.32rem] xs:tw-text-[.72rem]">Children up to Class 8</span><span className="tw-px-[.58rem] tw-py-[.3rem] tw-text-[.69rem] xs:tw-px-[.66rem] xs:tw-py-[.32rem] xs:tw-text-[.72rem]">Learning Support</span>
        </div>
      </div>
      <div className="sf-home-hero-art tw-relative tw-grid tw-min-h-[14rem] tw-place-content-center tw-overflow-hidden tw-p-8 tw-text-center nav:tw-min-h-[19rem]" role="img" aria-label="Illustration representing learning and opportunity">
        <div className="sf-demo-book" aria-hidden="true">📚</div>
        <strong>Learning opens doors.</strong>
        <span>Education · Opportunity · Brighter Futures</span>
      </div>
    </section>

    <section className="sf-home-pillars tw-relative tw-mx-auto tw-mt-[-1.35rem] tw-w-full tw-max-w-[1380px] tw-px-[5%] tw-pt-[clamp(2.8rem,5vw,4.4rem)] tw-pb-[clamp(1.5rem,2.5vw,2.25rem)]" aria-label="Foundation focus areas">
      <p className="sf-eyebrow tw-mb-[.65rem] tw-min-w-0">Our current focus: Education</p>
      <div className="sf-home-pillars-grid tw-grid tw-min-w-0 tw-grid-cols-1 tw-gap-[0.85rem] min-[641px]:tw-grid-cols-2">
        <div><strong>Free Education</strong><span>Making learning more accessible.</span></div>
        <div><strong>Learning Support</strong><span>Supporting learners&apos; educational journeys.</span></div>
      </div>
    </section>

    <section className="tw-mx-auto tw-w-full tw-max-w-[1380px] tw-px-[5%] tw-pt-[clamp(1.75rem,3vw,2.75rem)] tw-pb-[clamp(2.8rem,5vw,4.4rem)]">
      <div className="sf-section-heading tw-mb-[1.7rem] tw-max-w-[47rem]">
        <p className="sf-eyebrow">Who we are</p>
        <h2 className="tw-mb-[.9rem] tw-font-display tw-text-[clamp(1.9rem,3.2vw,2.5rem)] tw-font-bold tw-leading-[1.16]">Opportunity begins with access.</h2>
        <p className="tw-mb-5 tw-leading-[1.7]">Srisailam Foundation is a non-profit organisation focused on making education more accessible to children. Through free education and learning support for children studying up to Class 8, we aim to help learners build confidence, develop their potential and move towards a brighter future.</p>
        <p className="tw-mb-5 tw-leading-[1.7]">As the Foundation grows, we aspire to explore opportunities in skill development, women&apos;s empowerment, health, environmental awareness and livelihoods.</p>
        <Link className="sf-button sf-button-primary" to="/about/">Our Story</Link>
      </div>
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[641px]:tw-grid-cols-2 nav:tw-grid-cols-3">
        {focusAreas.map((area) => (
          <Link className="sf-home-card" to={area.path} key={area.title}>
            <i className={area.icon} aria-hidden="true"></i>
            <h3>{area.title}</h3>
            <p>{area.text}</p>
          </Link>
        ))}
      </div>
    </section>

    <section className="tw-mx-auto tw-w-full tw-max-w-[1380px] tw-px-[5%] tw-py-[clamp(2.8rem,5vw,4.4rem)]">
      <div className="sf-section-heading tw-mb-[1.7rem] tw-max-w-[47rem]">
        <p className="sf-eyebrow">Our focus &amp; future vision</p>
        <h2>Education today. Wider opportunity tomorrow.</h2>
        <p>Education is at the heart of our work today. Our long-term vision includes exploring other areas of community development as our capacity grows.</p>
      </div>
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[641px]:tw-grid-cols-2 min-[951px]:tw-grid-cols-3">
        {programmes.map((programme) => {
          const content = <>
            <i className={programme.icon} aria-hidden="true"></i>
            <span className={`sf-programme-status ${programme.status === 'Current focus' ? 'is-current' : ''}`}>{programme.status}</span>
            <h3>{programme.title}</h3>
            <p>{programme.text}</p>
          </>;
          return programme.path
            ? <Link className="sf-home-card" to={programme.path} key={programme.title}>{content}</Link>
            : <div className={`sf-home-card${programme.title === 'Skill Development' ? ' tw-cursor-default' : ''}`} key={programme.title}>{content}</div>;
        })}
      </div>
    </section>

    <section className="tw-mx-auto tw-grid tw-w-full tw-max-w-[1380px] tw-grid-cols-1 tw-items-center tw-gap-8 tw-px-[5%] tw-py-[clamp(2.8rem,5vw,4.4rem)] min-[951px]:tw-grid-cols-2 min-[951px]:tw-gap-[clamp(2rem,4vw,3.75rem)]">
      <div className="sf-home-vision-copy">
        <p className="sf-eyebrow">Our vision</p>
        <h2>A future with quality education and equal opportunity.</h2>
        <p className="tw-mb-[1.35rem] tw-max-w-[36rem] tw-leading-[1.75]">Our vision is a society where every individual has access to quality education, essential skills and opportunities to build a better future.</p>
        <p className="tw-mb-[1.35rem] tw-max-w-[36rem] tw-leading-[1.75]">We hope to reach more communities with educational support and explore other areas of community development as our capacity and resources grow.</p>
        <Link className="sf-button sf-button-primary" to="/education/">Explore our programmes</Link>
      </div>
      <div className="sf-home-vision-art sf-home-vision-photo tw-aspect-[3/2] tw-min-h-0 tw-overflow-hidden tw-p-0">
        <img
          src={visionImage}
          alt="Collage showing students and adults together outdoors and in an office setting"
          width="1600"
          height="1067"
          loading="lazy"
          decoding="async"
        />
      </div>
    </section>

    <section className="sf-home-stories" aria-labelledby="sf-home-stories-title">
      <div className="tw-mx-auto tw-w-full tw-max-w-[1380px] tw-px-[5%] tw-py-[clamp(2.8rem,5vw,4.4rem)]">
        <div className="sf-section-heading tw-mb-[1.7rem] tw-max-w-[47rem]">
          <p className="sf-eyebrow">Community voices</p>
          <h2 id="sf-home-stories-title">Stories and perspectives</h2>
          <p>Voices from people connected with Srisailam Foundation.</p>
        </div>
        <TestimonialSection variant="stories" showHeading={false} />
      </div>
    </section>

    <section className="sf-home-engagement tw-mx-auto tw-w-full tw-max-w-[1380px] tw-px-[5%] tw-py-[clamp(2.8rem,5vw,4.4rem)]" aria-labelledby="sf-home-engagement-title">
      <div className="sf-section-heading tw-mb-[1.7rem] tw-max-w-[47rem]">
        <p className="sf-eyebrow">Get involved</p>
        <h2 id="sf-home-engagement-title">Be the change you wish to see.</h2>
        <p>Join us in supporting free education and learning opportunities for children.</p>
      </div>
      <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[641px]:tw-grid-cols-2 min-[951px]:tw-grid-cols-3">
        <Link className="sf-home-card sf-engagement-card tw-flex tw-h-full tw-flex-col" to="/become-member/">
          <i className="fas fa-user-plus" aria-hidden="true"></i>
          <h3>Become a Member</h3>
          <p>Become part of the Foundation&apos;s journey and support children&apos;s education.</p>
          <span className="tw-mt-auto tw-pt-[1.1rem]">Become a member <i className="fas fa-arrow-right" aria-hidden="true"></i></span>
        </Link>
        <Link className="sf-home-card sf-engagement-card tw-flex tw-h-full tw-flex-col" to="/volunteer">
          <i className="fas fa-hands-helping" aria-hidden="true"></i>
          <h3>Volunteer</h3>
          <p>Share your time and skills to support children&apos;s learning.</p>
          <span className="tw-mt-auto tw-pt-[1.1rem]">Volunteer with us <i className="fas fa-arrow-right" aria-hidden="true"></i></span>
        </Link>
        <Link className="sf-home-card sf-engagement-card tw-flex tw-h-full tw-flex-col" to="/donate/">
          <i className="fas fa-donate" aria-hidden="true"></i>
          <h3>Support Our Work</h3>
          <p>Help strengthen access to education and learning support for children.</p>
          <span className="tw-mt-auto tw-pt-[1.1rem]">Donate now <i className="fas fa-arrow-right" aria-hidden="true"></i></span>
        </Link>
      </div>
    </section>

  </main>
);

export default HomePage;
